import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { RunningInstance } from 'src/common/interfaces/instances';
import type { NetworkInstanceStatus } from 'src/common/interfaces/networks/instances';
import type { VmTypes } from 'src/common/interfaces/vms';
import { TOOLTIP_ELEMENTS } from 'src/features/room/components/helper-tooltips/helper-tooltip.constants';
import type {
  TooltipElementsDataType,
  TutorialsTypes,
} from 'src/features/room/components/helper-tooltips/helper-tooltip.types';
import type { Ticket } from 'src/features/room/room.types';

export interface RoomState {
  taskStates: boolean[][];
  roomCode: string;
  isRoomCompleted: boolean;
  isStreakIncreased: boolean;
  isPathCompleted: boolean;
  vmStartType: VmTypes;
  activeMachine?: RunningInstance;
  helperTooltipElements?: TooltipElementsDataType;
  remAttackBoxBtn: boolean;
  hasUserJoinedRoom: boolean;
  networkDetails?: { _id: string; state?: string; status?: NetworkInstanceStatus } | null;
  vmUploadId?: string;
  vmTaskId?: string;
  isStreakRepaired?: boolean;
  ticketsAwarded: Ticket[] | null;
  isTicketDrawEligible?: boolean;
  hasReachedLimit?: boolean;
  echoAvatarState: {
    currentAnswer: boolean | null; // null = no answer, true = correct, false = wrong
    counts: {
      correct: number;
      wrong: number;
    };
    shouldTriggerAnimation: boolean;
  };
}

const initialState: RoomState = {
  taskStates: [],
  roomCode: '',
  isRoomCompleted: false,
  isStreakIncreased: false,
  isPathCompleted: false,
  vmStartType: 'AttackBox',
  helperTooltipElements: undefined,
  remAttackBoxBtn: true,
  hasUserJoinedRoom: false,
  networkDetails: undefined,
  vmUploadId: '',
  vmTaskId: '',
  isStreakRepaired: undefined,
  ticketsAwarded: null,
  isTicketDrawEligible: false,
  hasReachedLimit: false,
  echoAvatarState: {
    currentAnswer: null,
    counts: {
      correct: 0,
      wrong: 0,
    },
    shouldTriggerAnimation: false,
  },
};

const roomStoreSlice = createSlice({
  name: 'room',
  initialState,
  reducers: {
    setTaskStates: (state, action: PayloadAction<boolean[][]>) => ({ ...state, taskStates: action.payload }),
    updateTaskQuestionState: (state, action: PayloadAction<{ taskNo: number; questionNo: number }>) => {
      const newState = [...state.taskStates];
      if (state.taskStates.length >= action.payload.taskNo - 1) {
        newState[action.payload.taskNo - 1] = newState[action.payload.taskNo - 1]?.slice() ?? [false];
        if (newState[action.payload.taskNo - 1]?.[action.payload.questionNo - 1] !== undefined) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          // biome-ignore lint/style/noNonNullAssertion: Already checked for undefined above
          newState[action.payload.taskNo - 1]![action.payload.questionNo - 1] = true;
        }
      }

      return { ...state, taskStates: newState };
    },
    setRoomCode: (state, action: PayloadAction<string>) => ({ ...state, roomCode: action.payload }),
    setIsRoomComplete: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isRoomCompleted: action.payload,
    }),
    setIsStreakIncreased: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isStreakIncreased: action.payload,
    }),
    setIsPathComplete: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isPathCompleted: action.payload,
    }),
    setVmStartType: (state, action: PayloadAction<VmTypes>) => ({ ...state, vmStartType: action.payload }),
    setIsStreakRepaired: (state, action: PayloadAction<boolean>) => ({ ...state, isStreakRepaired: action.payload }),
    setActiveMachine: (state, action: PayloadAction<RunningInstance | undefined>) => ({
      ...state,
      activeMachine: action.payload,
    }),
    setHelperTooltipElements: (state, action: PayloadAction<TutorialsTypes | undefined>) => ({
      ...state,
      helperTooltipElements: action.payload ? TOOLTIP_ELEMENTS[action.payload] : undefined,
    }),
    setHasUserJoinedRoom: (state, action: PayloadAction<boolean>) => ({ ...state, hasUserJoinedRoom: action.payload }),
    setRemAttackBoxBtn: (state, action: PayloadAction<boolean>) => ({ ...state, remAttackBoxBtn: action.payload }),
    setNetworkDetails: (state, action: PayloadAction<{ _id: string; state?: string } | null | undefined>) => ({
      ...state,
      networkDetails: action.payload,
    }),
    setVmUploadId: (state, action: PayloadAction<string>) => ({ ...state, vmUploadId: action.payload }),
    setVmTaskId: (state, action: PayloadAction<string>) => ({ ...state, vmTaskId: action.payload }),
    setIsTicketDrawEligible: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isTicketDrawEligible: action.payload,
    }),
    setTicketsAwarded: (state, action: PayloadAction<Ticket[] | null>) => ({
      ...state,
      ticketsAwarded: action.payload,
    }),
    setHasReachedLimit: (state, action: PayloadAction<boolean>) => ({
      ...state,
      hasReachedLimit: action.payload,
    }),
    setEchoAnswer: (state, action: PayloadAction<boolean>) => {
      const newCounts = { ...state.echoAvatarState.counts };

      if (action.payload) {
        newCounts.correct += 1;
      } else {
        newCounts.wrong += 1;
      }

      return {
        ...state,
        echoAvatarState: {
          currentAnswer: action.payload,
          counts: newCounts,
          shouldTriggerAnimation: true,
        },
      };
    },
    clearEchoAnimation: (state) => ({
      ...state,
      echoAvatarState: {
        ...state.echoAvatarState,
        currentAnswer: null,
        shouldTriggerAnimation: false,
      },
    }),
    resetEchoState: (state) => ({
      ...state,
      echoAvatarState: {
        currentAnswer: null,
        counts: {
          correct: 0,
          wrong: 0,
        },
        shouldTriggerAnimation: false,
      },
    }),
  },
});

export const {
  setTaskStates,
  updateTaskQuestionState,
  setRoomCode,
  setIsRoomComplete,
  setIsStreakIncreased,
  setIsPathComplete,
  setIsStreakRepaired,
  setVmStartType,
  setActiveMachine,
  setHelperTooltipElements,
  setHasUserJoinedRoom,
  setRemAttackBoxBtn,
  setNetworkDetails,
  setVmUploadId,
  setVmTaskId,
  setTicketsAwarded,
  setIsTicketDrawEligible,
  setHasReachedLimit,
  setEchoAnswer,
  clearEchoAnimation,
  resetEchoState,
} = roomStoreSlice.actions;
export const roomReducer = roomStoreSlice.reducer;
